import { Section, SectionTitle, CaseCard, IMCard, StepCard } from './shared';

export function AllCasesContent() {
  const allCases = [
    {
      icon: '📱',
      title: '小红书自动运营',
      description: '使用 OpenClaw 实现小红书账号的自动化运营，包括内容发布、互动回复、数据分析等',
      tags: ['小红书', '自动化', '内容运营'],
      difficulty: 'intermediate' as const,
      link: 'https://curl.qcloud.com/Jh5l6fWJ',
    },
    {
      icon: '✍️',
      title: 'OpenClaw 实现小红书自动化发文：操作指南',
      description: '小红书自动化神器 xiaohongshu-mcp，支持一键发笔记、搜内容、写评论，日更50篇不封号。9.7k stars 开源项目，本地运行安全可靠。',
      tags: ['小红书', 'MCP', '自动发文', 'xiaohongshu-mcp'],
      difficulty: 'intermediate' as const,
      link: 'https://cloud.tencent.com/developer/article/2633552',
    },
    {
      icon: '📈',
      title: 'AI选股系统',
      description: '10分钟打造你的AI选股系统，OpenClaw每日自动筛选5支潜力股',
      tags: ['股票', 'AI分析', '自动化'],
      difficulty: 'intermediate' as const,
      link: 'https://curl.qcloud.com/GbASb3WE',
    },
    {
      icon: '💼',
      title: '飞书全家桶自动化',
      description: '5分钟让 OpenClaw 玩转飞书全家桶，实现文档、表格、日程的智能化管理',
      tags: ['飞书', '办公自动化', '效率工具'],
      difficulty: 'beginner' as const,
      link: 'https://curl.qcloud.com/PWo3Suxt',
    },
    {
      icon: '🍎',
      title: '苹果生态打通',
      description: '配置 Skills 打通苹果生态，实现 iPhone、iPad、Mac 之间的无缝协作',
      tags: ['Apple', 'iOS', 'macOS', '生态集成'],
      difficulty: 'advanced' as const,
      link: 'https://curl.qcloud.com/HT88w762',
    },
    {
      icon: '❤️',
      title: '专属营养师',
      description: '把 OpenClaw 训练成专属营养师，提供个性化的饮食建议和健康追踪',
      tags: ['健康', '营养', 'AI助手'],
      difficulty: 'beginner' as const,
      link: 'https://curl.qcloud.com/hdsYWvoY',
    },
    {
      icon: '☁️',
      title: 'CloudBase 自动化部署',
      description: '用 CloudBase 自动化开发一个网站并部署上线，实现全栈自动化',
      tags: ['CloudBase', '自动化部署', '全栈开发'],
      difficulty: 'advanced' as const,
      link: 'https://curl.qcloud.com/vIlzcNDW',
    },
  ];

  const quickLinks = [
    { icon: '🏠', title: '官网', description: 'OpenClaw 官方入口', link: 'https://curl.qcloud.com/s2A6QOhx', accent: 'from-cyan-500/20 to-cyan-500/5', border: 'border-cyan-500/30', iconBg: 'bg-cyan-500/10' },
    { icon: '📖', title: '文档', description: '一键部署图文教程', link: 'https://curl.qcloud.com/6ssRWU2v', accent: 'from-emerald-500/20 to-emerald-500/5', border: 'border-emerald-500/30', iconBg: 'bg-emerald-500/10' },
    { icon: '🎥', title: '视频', description: '视频版部署教程', link: 'https://curl.qcloud.com/lKzcVOgM', accent: 'from-violet-500/20 to-violet-500/5', border: 'border-violet-500/30', iconBg: 'bg-violet-500/10' },
    { icon: '🤖', title: '用AI部署', description: 'AI 助手一键部署', link: 'https://curl.qcloud.com/u224GaRm', accent: 'from-orange-500/20 to-orange-500/5', border: 'border-orange-500/30', iconBg: 'bg-orange-500/10' },
  ];

  const imPlatforms = [
    { icon: '💬', title: 'QQ', link: 'https://curl.qcloud.com/pXs8ucrE' },
    { icon: '💬', title: '企业微信', link: 'https://curl.qcloud.com/wfKhd8e2' },
    { icon: '💬', title: '飞书', link: 'https://curl.qcloud.com/v7ld2KZi' },
    { icon: '💬', title: '钉钉', link: 'https://curl.qcloud.com/ay13fHXz' },
    { icon: '💬', title: 'Telegram', link: 'https://curl.qcloud.com/u8JTMVQO' },
    { icon: '💬', title: 'Discord', link: 'https://curl.qcloud.com/RcR85gYW' },
  ];

  const enterpriseApps = [
    { emoji: '👥', title: '智能客服', desc: '自动化客户咨询回复，7×24小时在线服务', color: 'from-cyan-500/10 to-transparent', border: 'border-cyan-500/20' },
    { emoji: '📊', title: '数据分析', desc: '自动生成业务报告，数据可视化展示', color: 'from-emerald-500/10 to-transparent', border: 'border-emerald-500/20' },
    { emoji: '📱', title: '社媒运营', desc: '自动化内容发布、评论回复、数据分析', color: 'from-violet-500/10 to-transparent', border: 'border-violet-500/20' },
    { emoji: '💼', title: '办公自动化', desc: '会议纪要生成、日程管理、任务提醒', color: 'from-orange-500/10 to-transparent', border: 'border-orange-500/20' },
  ];

  return (
    <div className="space-y-16">

      {/* Hero Banner */}
      <div className="relative rounded-2xl overflow-hidden border border-slate-700/60 bg-gradient-to-br from-slate-800/80 via-slate-900 to-slate-950 p-10">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-cyan-500/10 via-transparent to-transparent pointer-events-none" />
        <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-xs font-medium mb-4">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse inline-block"></span>
              持续更新中
            </div>
            <h1 className="text-3xl font-bold text-white mb-3">OpenClaw 实战案例库</h1>
            <p className="text-slate-400 text-sm leading-relaxed max-w-xl">
              收录真实用户的 OpenClaw 使用案例，覆盖社交媒体、金融投资、办公自动化等多个场景，帮助你快速找到适合自己的玩法。
            </p>
          </div>
          <div className="flex gap-4 shrink-0">
            <div className="text-center">
              <div className="text-3xl font-bold text-cyan-400">7+</div>
              <div className="text-xs text-slate-500 mt-1">实战案例</div>
            </div>
            <div className="w-px bg-slate-700" />
            <div className="text-center">
              <div className="text-3xl font-bold text-emerald-400">6</div>
              <div className="text-xs text-slate-500 mt-1">接入平台</div>
            </div>
            <div className="w-px bg-slate-700" />
            <div className="text-center">
              <div className="text-3xl font-bold text-violet-400">4</div>
              <div className="text-xs text-slate-500 mt-1">企业场景</div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Deploy */}
      <Section>
        <SectionTitle id="quick-deploy">快速部署</SectionTitle>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          {quickLinks.map((item) => (
            <a
              key={item.title}
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              className={`group relative rounded-xl border ${item.border} bg-gradient-to-br ${item.accent} p-5 hover:scale-[1.03] transition-all duration-200 cursor-pointer block`}
            >
              <div className={`w-10 h-10 rounded-lg ${item.iconBg} flex items-center justify-center text-xl mb-3`}>
                {item.icon}
              </div>
              <div className="font-semibold text-white text-sm mb-1">{item.title}</div>
              <div className="text-xs text-slate-400">{item.description}</div>
              <i className="ri-arrow-right-up-line absolute top-4 right-4 text-slate-600 group-hover:text-slate-300 transition-colors text-sm"></i>
            </a>
          ))}
        </div>
      </Section>

      {/* Getting Started Steps */}
      <Section>
        <SectionTitle id="getting-started">快速上手四步走</SectionTitle>
        <div className="grid md:grid-cols-4 gap-4 mt-6">
          {[
            { number: '01', title: '选择部署方式', description: '腾讯云Lighthouse一键部署或AI助手部署', color: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/20' },
            { number: '02', title: '配置AI模型', description: '选择腾讯混元、DeepSeek等模型并配置API', color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20' },
            { number: '03', title: '接入IM应用', description: '配置QQ、企业微信、飞书或钉钉机器人', color: 'text-violet-400', bg: 'bg-violet-500/10', border: 'border-violet-500/20' },
            { number: '04', title: '开始使用', description: '在聊天应用中与OpenClaw对话，探索功能', color: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/20' },
          ].map((step, i) => (
            <div key={i} className={`relative rounded-xl border ${step.border} ${step.bg} p-6`}>
              <div className={`text-4xl font-black ${step.color} opacity-30 mb-3 leading-none`}>{step.number}</div>
              <h3 className="font-semibold text-white text-sm mb-2">{step.title}</h3>
              <p className="text-xs text-slate-400 leading-relaxed">{step.description}</p>
              {i < 3 && (
                <i className="ri-arrow-right-line absolute -right-3 top-1/2 -translate-y-1/2 text-slate-600 text-lg hidden md:block z-10"></i>
              )}
            </div>
          ))}
        </div>
      </Section>

      {/* IM Integration */}
      <Section>
        <div className="flex items-end justify-between mb-6">
          <div>
            <SectionTitle id="integration-guide">快速接入指南</SectionTitle>
            <p className="text-slate-500 text-sm mt-1">将 OpenClaw 接入你常用的即时通讯应用</p>
          </div>
        </div>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
          {imPlatforms.map((platform) => (
            <a
              key={platform.title}
              href={platform.link}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex flex-col items-center gap-2 p-4 rounded-xl border border-slate-700/60 bg-slate-800/40 hover:border-cyan-500/40 hover:bg-slate-800/80 transition-all cursor-pointer"
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-slate-700/60 group-hover:bg-cyan-500/10 transition-colors text-xl">
                {platform.icon}
              </div>
              <span className="text-xs font-medium text-slate-300 group-hover:text-white transition-colors whitespace-nowrap">{platform.title}</span>
            </a>
          ))}
        </div>
      </Section>

      {/* All Cases */}
      <Section>
        <div className="flex items-end justify-between mb-6">
          <div>
            <SectionTitle id="all-cases">OpenClaw 实战案例</SectionTitle>
            <p className="text-slate-500 text-sm mt-1">真实场景，开箱即用</p>
          </div>
          <div className="flex items-center gap-3 text-xs text-slate-500">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-400 inline-block"></span>初级</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-400 inline-block"></span>中级</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400 inline-block"></span>高级</span>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {allCases.map((caseItem, index) => (
            <CaseCard key={index} {...caseItem} />
          ))}
        </div>
      </Section>

      {/* What is OpenClaw */}
      <Section>
        <div className="rounded-2xl border border-cyan-500/20 bg-gradient-to-br from-cyan-500/5 via-slate-900 to-slate-900 p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-xl">🤖</div>
            <h3 className="text-xl font-bold text-white">什么是 OpenClaw？</h3>
          </div>
          <p className="text-slate-400 text-sm leading-relaxed mb-8 max-w-2xl">
            <strong className="text-white">OpenClaw</strong>（原名 Clawdbot）是一个开源的个人 AI 助手，可以 24/7 全天候运行在您的计算机或云服务器上。
            通过熟悉的聊天应用（如 QQ、企业微信、飞书、钉钉）与 AI 助手交互，让它帮您完成各种任务。
          </p>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { icon: '🤖', title: '智能对话', desc: '通过自然语言与AI助手交流，无需学习复杂命令', color: 'border-cyan-500/20 bg-cyan-500/5' },
              { icon: '🧠', title: '持久记忆', desc: '记住您的偏好和历史对话，提供个性化服务', color: 'border-emerald-500/20 bg-emerald-500/5' },
              { icon: '⚡', title: '主动执行', desc: '定时任务、提醒、后台任务自动执行', color: 'border-violet-500/20 bg-violet-500/5' },
            ].map((feat) => (
              <div key={feat.title} className={`rounded-xl border ${feat.color} p-5`}>
                <div className="text-2xl mb-3">{feat.icon}</div>
                <h4 className="font-semibold text-white text-sm mb-2">{feat.title}</h4>
                <p className="text-xs text-slate-400 leading-relaxed">{feat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Enterprise Applications */}
      <Section>
        <SectionTitle id="enterprise-apps">企业应用场景</SectionTitle>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
          {enterpriseApps.map((app) => (
            <div
              key={app.title}
              className={`rounded-xl border ${app.border} bg-gradient-to-b ${app.color} p-6 hover:scale-[1.02] transition-all duration-200`}
            >
              <div className="text-3xl mb-4">{app.emoji}</div>
              <h3 className="font-semibold text-white text-sm mb-2">{app.title}</h3>
              <p className="text-xs text-slate-400 leading-relaxed">{app.desc}</p>
            </div>
          ))}
        </div>
      </Section>

    </div>
  );
}
